/**
 * O histórico da paleta de busca (Story 18.1, AC-7).
 *
 * ⚠️ GUARDA `kind:id`, NUNCA NOMES NEM CAMINHOS. As três consequências são todas
 * desejadas, e nenhuma delas se obtém guardando o registo inteiro:
 *   • um registo RENOMEADO aparece com o nome novo — porque o nome é sempre
 *     relido do servidor, não do que se guardou;
 *   • um registo EXCLUÍDO não volta, e cai da lista sozinho;
 *   • um registo ARQUIVADO também não volta, porque a resolução por
 *     `GET /api/search?ids=` passa pelos MESMOS predicados de visibilidade da
 *     busca por texto.
 * Guardar o nome quebraria as três de uma vez, e a última em silêncio: a paleta
 * continuaria a oferecer atalho para um registo que a busca já esconde.
 *
 * ⚠️ NENHUMA FALHA DO `localStorage` PODE DERRUBAR A BUSCA. Modo privado, quota
 * cheia e um valor corrompido por outra aba são todos possíveis, e nenhum deles
 * é um erro do usuário. Cada função tem o seu `try/catch` e degrada em silêncio
 * para "não há histórico" — o histórico é conveniência; a busca é a
 * funcionalidade.
 */
import { SEARCH_KINDS, type SearchItem, type SearchKind } from '@/lib/api/global-search'

/** A chave do protótipo, mantida — é a que o desenho aprovado usa. */
export const SEARCH_HISTORY_KEY = 'caliber.busca.recentes'

/**
 * Sete, e é o número da AC.
 *
 * Fica confortavelmente abaixo do teto de 20 ids que o servidor impõe
 * (`MAX_SEARCH_IDS`), o que é o que permite resolver o histórico INTEIRO num só
 * pedido — e é por isso que a lista nunca aparece pela metade.
 */
export const SEARCH_HISTORY_MAX = 7

/** A referência de um registo, no formato que viaja em `?ids=`. */
export type SearchRef = `${SearchKind}:${string}`

/** `kind:id` — a única forma em que um registo entra no histórico. */
export function searchItemRef(item: Pick<SearchItem, 'kind' | 'id'>): SearchRef {
  return `${item.kind}:${item.id}`
}

const KINDS: readonly string[] = SEARCH_KINDS

/**
 * Uma referência é válida se tem entidade CONHECIDA e id não vazio.
 *
 * A validação existe porque este valor é de fora: outra aba, uma versão
 * anterior do produto, ou alguém a mexer no `localStorage`. Uma entidade que já
 * não existe passaria a viajar em `?ids=` e o servidor responderia 400 — ou
 * seja, um valor velho no disco derrubava a lista de recentes inteira. Filtrar
 * na leitura é mais barato que descobrir isso pelo 400.
 */
function isValidRef(valor: unknown): valor is SearchRef {
  if (typeof valor !== 'string') return false
  const separador = valor.indexOf(':')
  if (separador === -1) return false
  return KINDS.includes(valor.slice(0, separador)) && valor.slice(separador + 1).length > 0
}

function readRaw(): SearchRef[] {
  try {
    const raw = window.localStorage.getItem(SEARCH_HISTORY_KEY)
    if (!raw) return []
    const lidos: unknown = JSON.parse(raw)
    if (!Array.isArray(lidos)) return []
    return lidos.filter(isValidRef).slice(0, SEARCH_HISTORY_MAX)
  } catch {
    // Modo privado, quota, JSON corrompido: sem histórico, e a busca segue.
    return []
  }
}

function writeRaw(refs: readonly SearchRef[]): void {
  try {
    window.localStorage.setItem(
      SEARCH_HISTORY_KEY,
      JSON.stringify(refs.slice(0, SEARCH_HISTORY_MAX)),
    )
  } catch {
    // Idem. Um histórico que não grava é um histórico vazio, não um erro.
  }
}

/** As referências guardadas, da mais recente para a mais antiga. */
export function readSearchHistory(): SearchRef[] {
  return readRaw()
}

/**
 * Põe o registo no topo.
 *
 * Abrir um registo que já lá está MOVE-O em vez de o duplicar: o histórico é
 * uma lista de sítios, não um diário de visitas.
 */
export function rememberSearchRecord(item: Pick<SearchItem, 'kind' | 'id'>): SearchRef[] {
  const ref = searchItemRef(item)
  const proximos = [ref, ...readRaw().filter((r) => r !== ref)].slice(0, SEARCH_HISTORY_MAX)
  writeRaw(proximos)
  return proximos
}

/** Esquece UM registo — o `×` do rato e o `⌫` do teclado chamam isto. */
export function forgetSearchRecord(ref: string): SearchRef[] {
  const proximos = readRaw().filter((r) => r !== ref)
  writeRaw(proximos)
  return proximos
}

/** Esquece todos — o "Limpar" do cabeçalho de "Recentes". */
export function clearSearchHistory(): SearchRef[] {
  try {
    window.localStorage.removeItem(SEARCH_HISTORY_KEY)
  } catch {
    /* idem */
  }
  return []
}

/**
 * Reordena os itens resolvidos pela ORDEM GUARDADA.
 *
 * ⚠️ ISTO NÃO É COSMÉTICA. O servidor devolve os itens por RELEVÂNCIA, agrupados
 * por entidade — ele não conhece, nem pode conhecer, a ordem em que esta pessoa
 * abriu estes registos. Sem esta função, "Recentes" sai ordenado por ranking, e
 * a primeira linha da lista deixa de ser o último registo aberto: um rótulo que
 * diz "Recentes" sobre uma ordem que não é de recência nenhuma.
 *
 * O que não resolveu (excluído, arquivado, ou invisível para quem procura)
 * simplesmente não aparece — e é assim que a lista se limpa sozinha.
 */
export function orderByHistory(
  refs: readonly string[],
  items: readonly SearchItem[],
): SearchItem[] {
  const porRef = new Map<string, SearchItem>(items.map((item) => [searchItemRef(item), item]))
  const ordenados: SearchItem[] = []
  for (const ref of refs) {
    const item = porRef.get(ref)
    if (item) ordenados.push(item)
  }
  return ordenados
}
